import { formatDistanceToNow } from 'date-fns';
import { Loader2 } from 'lucide-react';

import { formatHoursWanted } from '@apps/web/components/caregivers/caregiver_scheduling_profile_format';
import { Badge } from '@apps/web/components/ui/badge';
import { useCaregiverSchedulingProfile } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';

type CaregiverSchedulingProfileProps = {
    readonly organizationId: string;
    readonly caregiverId: string | null;
};

const LABELS: Record<string, string> = {
    days_available: 'Days',
    time_blocks: 'Times',
    hours_wanted: 'Hours wanted',
    home_area: 'Home area',
    transportation: 'Transportation',
    languages: 'Client languages',
    service_preferences: 'Care preferences',
    other_employment: 'Other employment',
};

function humanize(value: string): string {
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatValue(key: string, value: unknown): string {
    if (key === 'hours_wanted' && value) {
        return formatHoursWanted(String(value));
    }
    if (Array.isArray(value)) {
        return value.map((item) => humanize(String(item))).join(', ');
    }
    return value ? humanize(String(value)) : 'Not collected';
}

export function CaregiverSchedulingProfile({
    organizationId,
    caregiverId,
}: CaregiverSchedulingProfileProps) {
    const { data, error, isLoading } = useCaregiverSchedulingProfile(
        organizationId,
        caregiverId,
    );

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2 py-4 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading scheduling preferences…
            </div>
        );
    }

    // No survey yet: render nothing rather than a placeholder card.
    if (error instanceof ApiError && error.status === 404) {
        return null;
    }

    if (!data || error) {
        return null;
    }

    const transportation = data.transportation_modes?.length
        ? data.transportation_modes
        : data.transportation;
    const rows = [
        ['days_available', data.days_available],
        ['time_blocks', data.time_blocks],
        ['hours_wanted', data.hours_wanted],
        [
            'home_area',
            [data.home_city, data.home_state, data.home_postal_code]
                .filter(Boolean)
                .join(', ') || data.home_area_input,
        ],
        ['transportation', transportation],
        ['languages', data.languages],
        ['service_preferences', data.service_preferences],
        ['other_employment', data.other_employment],
    ] as const;

    return (
        <section className="min-w-0">
            <header className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">
                        Scheduling preferences
                    </h3>
                    <Badge
                        variant={data.completed_at ? 'default' : 'secondary'}
                    >
                        {data.completed_at ? 'Complete' : 'Partial'}
                    </Badge>
                </div>
                <div className="text-muted-foreground text-xs">
                    Revision {data.revision}
                    {data.last_confirmed_at
                        ? ` · Confirmed ${formatDistanceToNow(
                              new Date(data.last_confirmed_at),
                              { addSuffix: true },
                          )}`
                        : ''}
                </div>
            </header>
            <dl className="mt-3 grid sm:grid-cols-2 sm:gap-x-4">
                {rows.map(([key, value]) => {
                    const provenance =
                        data.field_metadata[
                            key === 'transportation' &&
                            data.transportation_modes?.length
                                ? 'transportation_modes'
                                : key
                        ];
                    return (
                        <div
                            key={key}
                            className="border-border/70 border-b py-2.5"
                        >
                            <dt className="text-muted-foreground text-xs font-medium">
                                {LABELS[key]}
                            </dt>
                            <dd className="mt-1 text-sm">
                                {formatValue(key, value)}
                            </dd>
                            {provenance && (
                                <dd className="text-muted-foreground mt-1 text-xs">
                                    {humanize(provenance.source)} ·{' '}
                                    {formatDistanceToNow(
                                        new Date(provenance.updated_at),
                                        { addSuffix: true },
                                    )}
                                </dd>
                            )}
                        </div>
                    );
                })}
            </dl>
            {data.incomplete_question_keys.length > 0 && (
                <p className="text-muted-foreground mt-3 text-xs">
                    Still needed:{' '}
                    {data.incomplete_question_keys
                        .map((key) => LABELS[key] ?? humanize(key))
                        .join(', ')}
                </p>
            )}
        </section>
    );
}
